import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, CircleDot, Sparkles, Calendar, Clock, Tag, Plus, Search, Zap, Lightbulb } from "lucide-react";
import { MealSuggestion } from "@/types/mealSuggestion";
import { Recipe } from "@/types/recipe";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { SimilarRecipeCard } from "./SimilarRecipeCard";
import { SimilarityMatch } from "@/utils/recipeSimilarity";
import { useIsMobile } from "@/hooks/use-mobile";

interface MealSuggestionCardProps {
  suggestion: MealSuggestion;
  existingRecipe?: Recipe;
  usageCount?: number;
  similarMatches?: SimilarityMatch[];
  exactMatch?: Recipe;
  acceptingId?: string;
  onAccept: (suggestion: MealSuggestion) => void;
  onSearch: (suggestion: MealSuggestion) => void;
  onAcceptSimilar: (suggestion: MealSuggestion, recipe: Recipe) => void;
}

export function MealSuggestionCard({
  suggestion,
  existingRecipe,
  usageCount = 0,
  similarMatches = [],
  exactMatch,
  acceptingId,
  onAccept,
  onSearch,
  onAcceptSimilar,
}: MealSuggestionCardProps) {
  const isMobile = useIsMobile();

  const renderRecipeTitle = () => {
    // Always display the AI-suggested recipe name
    const title = suggestion.recipeName;
    
    if (suggestion.isFromCollection && suggestion.recipeId) {
      return (
        <Link to={`/recipe/${suggestion.recipeId}`} className="hover:text-primary transition-colors">
          {title}
        </Link>
      );
    } else if (existingRecipe) {
      return (
        <Link to={`/recipe/${existingRecipe.id}`} className="hover:text-primary transition-colors">
          {title}
        </Link>
      );
    } else {
      return title;
    }
  };

  const renderTags = () => {
    const basicTags = existingRecipe?.tags || suggestion.newRecipeDetails?.tags;
    const allTags = [];

    // Add quick meal tag if applicable
    if (existingRecipe?.isQuickRecipe) {
      allTags.push(
        <Badge key="quick-meal" variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
          <Zap className="h-3 w-3 mr-1" />
          Quick meal
        </Badge>
      );
    }

    // Add AI suggestion tag for imported suggestions
    if (!suggestion.isFromCollection && existingRecipe) {
      allTags.push(
        <Badge key="ai-suggestion" variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50">
          <Bot className="h-3 w-3 mr-1" />
          AI-suggestion
        </Badge>
      );
    } else if (existingRecipe?.suggestion_id) {
      allTags.push(
        <Badge key="ai-suggestion" variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50">
          <Bot className="h-3 w-3 mr-1" />
          AI-suggestion
        </Badge>
      );
    }

    // Add usage statistics
    if (existingRecipe) {
      const usageText = usageCount === 0 ? "Never used" : `Used ${usageCount} time${usageCount === 1 ? '' : 's'}`;
      const usageColor = usageCount === 0 ? "border-orange-300 text-orange-700 bg-orange-50" : "border-green-300 text-green-700 bg-green-50";
      
      allTags.push(
        <Badge key="usage-count" variant="outline" className={`text-xs ${usageColor}`}>
          <CircleDot className="h-3 w-3 mr-1" />
          {usageText}
        </Badge>
      );
    }

    // Add basic recipe tags
    if (basicTags && basicTags.length > 0) {
      basicTags.forEach((tag, idx) => {
        allTags.push(
          <Badge key={`basic-${idx}`} variant="outline" className="text-xs">
            {tag}
          </Badge>
        );
      });
    }
    
    if (allTags.length > 0) {
      return (
        <div className="flex items-center gap-1">
          <Tag className="h-3 w-3 md:h-4 md:w-4" />
          <div className="flex flex-wrap gap-1">
            {allTags}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="relative">
      <CardHeader className="pb-3 md:pb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">
            {suggestion.targetDate && !isNaN(new Date(suggestion.targetDate).getTime())
              ? format(new Date(suggestion.targetDate), 'EEEE MMM d')
              : 'Invalid Date'}
          </span>
          <span className="text-muted-foreground">•</span>
          <Badge variant={
            suggestion.isFromCollection ? "default" : 
            existingRecipe ? "default" : 
            "destructive"
          }>
            {suggestion.isFromCollection ? (exactMatch ? "Auto-matched Recipe" : "Your Recipe") : 
             existingRecipe ? "New Recipe" : 
             "New Suggestion"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 md:px-6">
        <div>
          <h3 className="font-semibold text-base md:text-lg mb-2 leading-tight">
            {renderRecipeTitle()}
          </h3>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{suggestion.reasoning}</p>
        </div>

        {/* Show details for both new recipes and existing recipes */}
        {(suggestion.newRecipeDetails || existingRecipe) && (
          <div className="space-y-2">
            {suggestion.newRecipeDetails && (
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                {suggestion.newRecipeDetails.description}
              </p>
            )}
            <div className={`${isMobile ? 'space-y-2' : 'flex items-center gap-4'} text-xs md:text-sm text-muted-foreground`}>
              {/* Show prep time for new recipes */}
              {suggestion.newRecipeDetails?.estimatedPrepTime && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 md:h-4 md:w-4" />
                  {suggestion.newRecipeDetails.estimatedPrepTime}
                </div>
              )}
              
              {renderTags()}
            </div>
          </div>
        )}

        {/* Search & Add Recipe for new suggestions with similar matches */}
        {!suggestion.isFromCollection && !existingRecipe && similarMatches.length > 0 && (
          <div className="pt-2 border-t">
            <Button
              onClick={() => onSearch(suggestion)}
              size={isMobile ? "default" : "sm"}
              variant="destructive"
              className={isMobile ? "w-full" : ""}
            >
              <Search className="h-4 w-4 mr-1" />
              {isMobile ? 'Search Recipe' : 'Search & Add Recipe'}
            </Button>
          </div>
        )}

        {/* Similar recipes from your collection */}
        {similarMatches.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Similar from your collection:</span>
            </div>
            <div className="grid gap-2">
              {similarMatches.slice(0, 3).map((match, idx) => (
                <SimilarRecipeCard
                  key={`${suggestion.id}-similar-${idx}`}
                  similarityMatch={match}
                  onSelect={(recipe) => onAcceptSimilar(suggestion, recipe)}
                  isCompact={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex gap-2'} pt-2`}>
          {/* New suggestions - only show Search & Add Recipe if no similar matches (otherwise shown above) */}
          {!suggestion.isFromCollection && !existingRecipe && similarMatches.length === 0 && (
            <Button
              onClick={() => onSearch(suggestion)}
              size={isMobile ? "default" : "sm"}
              variant="destructive"
              className={isMobile ? "w-full" : ""}
            >
              <Search className="h-4 w-4 mr-1" />
              {isMobile ? 'Search Recipe' : 'Search & Add Recipe'}
            </Button>
          )}
          
          {/* Collection recipes and imported suggestions - only show Accept */}
          {(suggestion.isFromCollection || existingRecipe) && (
            <Button
              onClick={() => onAccept(suggestion)}
              disabled={acceptingId === suggestion.targetDate}
              size={isMobile ? "default" : "sm"}
              className={isMobile ? "w-full" : ""}
            >
              <Plus className="h-4 w-4 mr-1" />
              {acceptingId === suggestion.targetDate ? 'Adding...' : 'Accept'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}